import React, { useState } from "react";
import { Sparkles, Loader2, FileText, Search, MessageSquare, Palette } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const TABS = [
  { id: "content", label: "Content Writer", icon: FileText },
  { id: "seo", label: "SEO Writer", icon: Search },
  { id: "logo", label: "Logo Concepts", icon: Palette },
  { id: "chat", label: "AI Chatbot", icon: MessageSquare },
];

export default function AITools() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("content");
  if (!user) {
    return (
      <div className="section text-center">
        <h1 className="text-3xl font-bold text-white" style={{fontFamily:'Outfit'}}>Login to access AI Tools</h1>
        <button onClick={()=>nav("/login?next=/ai-tools")} className="btn-primary mt-5">Login</button>
      </div>
    );
  }
  return (
    <div className="section">
      <div className="text-center mb-8">
        <div className="tag mb-3">AI Tools Suite</div>
        <h1 className="text-5xl font-black tracking-tighter" style={{fontFamily:'Outfit'}}>Your <span className="text-gradient-cyan">creative arsenal</span>.</h1>
      </div>
      <div className="flex justify-center gap-2 mb-8 flex-wrap" data-testid="ai-tools-tabs">
        {TABS.map(t => (
          <button key={t.id} onClick={()=>setTab(t.id)} data-testid={`tool-tab-${t.id}`}
            className={`px-4 py-2 rounded-md text-sm inline-flex items-center gap-2 ${tab===t.id ? "bg-cyan-400 text-black font-semibold" : "border border-white/10 text-gray-300 hover:text-white"}`}>
            <t.icon size={14}/> {t.label}
          </button>
        ))}
      </div>
      {tab === "content" && <ContentWriter />}
      {tab === "seo" && <SEOWriter />}
      {tab === "logo" && <LogoTool />}
      {tab === "chat" && <ChatTool />}
    </div>
  );
}

function ContentWriter() {
  const [topic, setTopic] = useState("");
  const [tone, setTone] = useState("professional");
  const [length, setLength] = useState("medium");
  const [out, setOut] = useState("");
  const [loading, setLoading] = useState(false);
  const go = async () => {
    if (!topic) return;
    setLoading(true);
    try {
      const { data } = await api.post("/ai/content-writer", { topic, tone, length });
      setOut(data.content);
    } catch (e) { toast.error("Failed"); }
    finally { setLoading(false); }
  };
  return (
    <div className="grid md:grid-cols-2 gap-5">
      <div className="card p-6 space-y-3">
        <label className="label">Topic</label>
        <input data-testid="content-topic" value={topic} onChange={e=>setTopic(e.target.value)} className="input w-full" placeholder="The future of AI in marketing..." />
        <div className="grid grid-cols-2 gap-3">
          <div><label className="label">Tone</label><select value={tone} onChange={e=>setTone(e.target.value)} className="input w-full"><option>professional</option><option>casual</option><option>luxury</option><option>technical</option></select></div>
          <div><label className="label">Length</label><select value={length} onChange={e=>setLength(e.target.value)} className="input w-full"><option value="short">Short</option><option value="medium">Medium</option><option value="long">Long</option></select></div>
        </div>
        <button onClick={go} disabled={loading} className="btn-primary inline-flex items-center gap-2" data-testid="content-generate">
          {loading ? <Loader2 size={16} className="animate-spin"/> : <Sparkles size={16}/>} Generate
        </button>
      </div>
      <div className="card p-6 min-h-[200px] whitespace-pre-wrap text-gray-200 text-sm leading-relaxed" data-testid="content-output">
        {out || <span className="text-gray-500">Your content will appear here…</span>}
      </div>
    </div>
  );
}

function SEOWriter() {
  const [topic, setTopic] = useState("");
  const [keyword, setKeyword] = useState("");
  const [out, setOut] = useState(null);
  const [loading, setLoading] = useState(false);
  const go = async () => {
    if (!topic || !keyword) return;
    setLoading(true);
    try {
      const { data } = await api.post("/ai/seo-writer", { page_topic: topic, target_keyword: keyword });
      setOut(data);
    } catch { toast.error("Failed"); }
    finally { setLoading(false); }
  };
  return (
    <div className="grid md:grid-cols-2 gap-5">
      <div className="card p-6 space-y-3">
        <label className="label">Page Topic</label>
        <input value={topic} onChange={e=>setTopic(e.target.value)} className="input w-full" placeholder="Best AI website builders 2026" data-testid="seo-topic"/>
        <label className="label">Target Keyword</label>
        <input value={keyword} onChange={e=>setKeyword(e.target.value)} className="input w-full" placeholder="ai website builder" data-testid="seo-keyword"/>
        <button onClick={go} disabled={loading} className="btn-primary inline-flex items-center gap-2" data-testid="seo-generate">
          {loading ? <Loader2 size={16} className="animate-spin"/> : <Sparkles size={16}/>} Generate
        </button>
      </div>
      <div className="card p-6 text-sm">
        {!out && <span className="text-gray-500">Your SEO brief will appear here…</span>}
        {out && (
          <div className="space-y-3 text-gray-200">
            {out.title && <div><div className="text-cyan-400 text-xs uppercase tracking-wider">Title</div>{out.title}</div>}
            {out.meta_description && <div><div className="text-cyan-400 text-xs uppercase tracking-wider">Meta</div>{out.meta_description}</div>}
            {out.h1 && <div><div className="text-cyan-400 text-xs uppercase tracking-wider">H1</div>{out.h1}</div>}
            {out.h2s && <div><div className="text-cyan-400 text-xs uppercase tracking-wider">H2s</div><ul className="list-disc pl-5">{out.h2s.map((h,i)=><li key={i}>{h}</li>)}</ul></div>}
            {out.keywords && <div className="flex flex-wrap gap-2">{out.keywords.map((k,i)=><span key={i} className="tag">{k}</span>)}</div>}
          </div>
        )}
      </div>
    </div>
  );
}

function LogoTool() {
  const [brand, setBrand] = useState("");
  const [industry, setIndustry] = useState("");
  const [style, setStyle] = useState("modern");
  const [out, setOut] = useState(null);
  const [loading, setLoading] = useState(false);
  const go = async () => {
    if (!brand) return;
    setLoading(true);
    try { const { data } = await api.post("/ai/logo-concept", { brand_name: brand, industry, style }); setOut(data); }
    catch { toast.error("Failed"); }
    finally { setLoading(false); }
  };
  return (
    <div className="grid md:grid-cols-2 gap-5">
      <div className="card p-6 space-y-3">
        <label className="label">Brand</label><input value={brand} onChange={e=>setBrand(e.target.value)} className="input w-full" data-testid="logo-brand"/>
        <label className="label">Industry</label><input value={industry} onChange={e=>setIndustry(e.target.value)} className="input w-full" data-testid="logo-industry"/>
        <label className="label">Style</label>
        <select value={style} onChange={e=>setStyle(e.target.value)} className="input w-full"><option>modern</option><option>minimal</option><option>luxury</option><option>playful</option><option>futuristic</option></select>
        <button onClick={go} disabled={loading} className="btn-primary inline-flex items-center gap-2" data-testid="logo-generate">{loading ? <Loader2 size={16} className="animate-spin"/> : <Sparkles size={16}/>} Generate</button>
      </div>
      <div className="card p-6 text-sm space-y-4">
        {!out && <span className="text-gray-500">Your logo concepts will appear here…</span>}
        {out?.concepts && out.concepts.map((c,i)=>(
          <div key={i} className="border border-white/10 rounded-lg p-4 bg-white/[0.02]">
            <div className="text-white font-semibold">{c.name}</div>
            <div className="text-gray-400 text-sm mt-1">{c.description}</div>
            {c.palette && <div className="flex gap-2 mt-2">{c.palette.map((p,j)=><div key={j} style={{background:p}} className="w-8 h-8 rounded border border-white/10"/>)}</div>}
            <div className="text-xs text-gray-500 mt-2">Typography: {c.typography} · Icon: {c.icon_idea}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ChatTool() {
  const [msgs, setMsgs] = useState([{ by: "ai", text: "Hi! I'm Optivize AI. How can I help today?" }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const session = React.useRef(`chat-${Math.random().toString(36).slice(2,9)}`);
  const send = async () => {
    if (!input.trim()) return;
    const userMsg = { by: "user", text: input };
    setMsgs((m)=>[...m, userMsg]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", { session_id: session.current, message: userMsg.text });
      setMsgs((m)=>[...m, { by: "ai", text: data.reply }]);
    } catch { setMsgs((m)=>[...m, { by: "ai", text: "Sorry, I hit an error." }]); }
    finally { setLoading(false); }
  };
  return (
    <div className="card p-6 max-w-3xl mx-auto">
      <div className="h-[400px] overflow-y-auto space-y-3 pr-2" data-testid="chat-window">
        {msgs.map((m,i) => (
          <div key={i} className={`flex ${m.by === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`px-4 py-2 rounded-2xl max-w-[80%] text-sm ${m.by === "user" ? "bg-cyan-400 text-black" : "bg-white/5 text-gray-200 border border-white/10"}`}>{m.text}</div>
          </div>
        ))}
        {loading && <div className="text-gray-500 text-sm">Optivize AI is typing…</div>}
      </div>
      <div className="flex gap-2 mt-4">
        <input data-testid="chat-input" value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&send()} className="input flex-1" placeholder="Ask anything about your project…"/>
        <button onClick={send} className="btn-primary" data-testid="chat-send">Send</button>
      </div>
    </div>
  );
}
